import type { enums } from "@fraction/shared";
import React, { type ReactNode, useCallback } from "react";
import Checkbox from "src/components/Checkbox";
import Skeleton from "src/components/Skeleton";
import { SemiBoldText } from "src/components/v1/Text";
import { cn } from "src/utilities/shadcnUtils";
import { imageMap } from "./Images";

// only some of those enums have a corresponding illust svg, refer to
// https://app.clickup.com/t/1zhpn5j or https://www.figma.com/file/3tHMTslIoZ7n4YbOlyud3V/Icons-Redesign for available enums
export type IllustrativeOptionType =
  | enums.PropertyType
  | enums.PropertyUsage
  | enums.UseOfFunds
  | enums.IncomeType
  | enums.DebtType
  | enums.FinancialAssetType
  | enums.CreditScoreGuess
  | "checkmark";

export type IllustrativeOptionProps = {
  value: IllustrativeOptionType;
  label: string;
  isMulti?: boolean;
  selected?: boolean;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  image?: ReactNode;
};

const IllustrativeOption = ({
  value,
  label,
  image,
  selected,
  isMulti = false,
  onClick,
  className,
  disabled,
}: IllustrativeOptionProps) => {
  const moduleName = value;

  const Svg = image ? null : imageMap[moduleName];

  const handleSelect = useCallback(() => {
    if (!disabled) {
      onClick?.();
    }
  }, [disabled, onClick]);

  const onKeyPress = useCallback(
    (evt: React.KeyboardEvent<HTMLDivElement>) => {
      if (evt.code === "Enter") {
        handleSelect();
      }
    },
    [handleSelect]
  );

  const isLongLabel = label.length > 28;

  return image || Svg ? (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        "group/option bg-white border border-solid border-gray-300 rounded-[10px] box-border flex cursor-pointer flex-col items-center text-center relative w-[calc(105.75px*1.4)] h-[calc(126px*1.4)] hover:border-green-400",
        selected && "border-green-500 outline outline-[3px] outline-green-500 -outline-offset-4",
        disabled && "opacity-80 hover:border-gray-300 cursor-not-allowed",
        className
      )}
      onClick={handleSelect}
      onKeyPress={onKeyPress}
    >
      {isMulti && <Checkbox checked={selected} label="" className="absolute top-4 -right-[3px] z-[1]" />}
      <div
        className={cn(
          "relative w-full flex flex-col justify-center items-center",
          isLongLabel ? "top-[10%]" : "top-[19%]"
        )}
      >
        {image ? (
          <img src={image as any} className="w-[54%] aspect-[100/102]" alt={value} />
        ) : Svg ? (
          <Svg width="54%" height="100%" className="w-[54%] aspect-[100/102]" alt={value} />
        ) : null}
        <p className="leading-[1.2em] mx-[0.8em] mt-[0.8em] text-[13px]">
          <SemiBoldText className="text-[length:inherit]">{label}</SemiBoldText>
        </p>
      </div>
    </div>
  ) : (
    <Skeleton width={105.75 * 1.2} height={126 * 1.2} />
  );
};

export default React.memo(IllustrativeOption);
